import React, { useCallback, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';

import { FormAction } from 'core/store/offer-store/offer-store';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import { DataPulls } from 'core/helpers/constants';
import { getMVRCheckStatus } from 'core/helpers/mvr-helpers';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useStore } from 'core/store/store.mobx';
import { Radio } from 'core/components/radio';
import ConfirmPullMVRModal from './confirm-pull-mvr-modal';
import { setTouchedFromErrors } from 'core/components/validation-errors-modal';
import useStyles from '../footer.styles';

const DataPullsAppcuesID = '1611e7bd-2a8b-4f28-844c-c6ccaf5f80fe';

const dataPullsToButtonLabel = {
  [DataPulls.NonMVRPulls]: 'Run Non-MVR reports',
  [DataPulls.AllPulls]: 'Run all reports'
};

const getButtonLabel = ({ hasAtLeastMVRToPull, hasOnlyMVRToRun, selectedDataPulls }) => {
  if (hasAtLeastMVRToPull) {
    // based on user's selection of the radio button
    return dataPullsToButtonLabel[selectedDataPulls];
  }

  if (hasOnlyMVRToRun) {
    return 'Run MVR reports';
  }

  return 'Run all reports';
};

const getDataToPull = ({ hasAtLeastMVRToPull, selectedDataPulls, hasOnlyMVRToRun }) => {
  if (hasOnlyMVRToRun || (hasAtLeastMVRToPull && selectedDataPulls === DataPulls.AllPulls)) {
    return [DataPulls.MvrPulls, DataPulls.NonMVRPulls];
  }
  if (hasAtLeastMVRToPull && selectedDataPulls === DataPulls.NonMVRPulls) {
    return [DataPulls.NonMVRPulls];
  }
  // undefined data pulls means we will pull everything
  return undefined;
};

const FillSkippedDataPulls = observer(({ copyLink, setShowValidationErrorsModal }) => {
  const { offer: store } = useStore();
  const classes = useStyles();
  const history = useHistory();
  const [selectedDataPulls, setSelectedDataPulls] = useState(DataPulls.NonMVRPulls);
  const [showConfirmPullMVRModal, setShowConfirmPullMVRModal] = useState(false);
  const { dirty, values, validateForm, setErrors, setTouched } = useFormikContext();
  const { user } = useSession();

  const { hasOnlyMVRToRun, hasAtLeastMVRToPull } = getMVRCheckStatus(store, dirty, values);

  const onConfirm = useCallback(() => {
    setShowConfirmPullMVRModal(false);
    track('Ran reports for applicant', {
      reportType: hasOnlyMVRToRun ? DataPulls.MvrPulls : selectedDataPulls,
      offerId: store.offer.id,
      agent: user.username
    });

    const dataToPull = getDataToPull({ hasAtLeastMVRToPull, selectedDataPulls, hasOnlyMVRToRun });
    store.fillOutSkippedDataPulls(history, dataToPull);
  }, [store, history, hasOnlyMVRToRun, selectedDataPulls, user, hasAtLeastMVRToPull]);

  const onClick = useCallback(async () => {
    // Always run validations before filling skipped data pulls
    const errors = await validateForm();
    if (Object.keys(errors).length) {
      setErrors(errors);
      // Convert errors object to touched structure (boolean flags instead of error messages)
      const touched = setTouchedFromErrors(errors);
      setTouched(touched);
      setShowValidationErrorsModal(true);
      return;
    }

    // if we're pulling MVR, show confirmation modal for Michigan
    if (hasOnlyMVRToRun || (hasAtLeastMVRToPull && selectedDataPulls === DataPulls.AllPulls)) {
      // Only show confirmation modal for Michigan
      if (store.state === 'MI') {
        setShowConfirmPullMVRModal(true);
      } else {
        onConfirm();
      }
    } else {
      onConfirm();
    }
  }, [
    hasOnlyMVRToRun,
    selectedDataPulls,
    onConfirm,
    hasAtLeastMVRToPull,
    validateForm,
    setErrors,
    setTouched,
    setShowValidationErrorsModal,
    store.state
  ]);

  useEffect(() => {
    if (hasOnlyMVRToRun && !dirty) {
      store.setFormAction({ action: FormAction.FillSkippedDataPullsAll });
    }

    // set the store action on first render
  }, []);

  return (
    <>
      {showConfirmPullMVRModal && (
        <ConfirmPullMVRModal
          onClose={() => setShowConfirmPullMVRModal(false)}
          open={showConfirmPullMVRModal}
          onConfirm={onConfirm}
        />
      )}
      <div className={classes.titleContainer}>
        <Label className={classes.title} type="subtitle">
          {hasOnlyMVRToRun ? 'We’ll need to run reports on driving records' : 'You are being shown an estimated price.'}
        </Label>
        <Label className={classes.subtitle} type="subtitle">
          {hasOnlyMVRToRun
            ? 'We will need to pull reports on motor vehicle records (MVR) to get your final price.'
            : 'In order to get you an accurate price, we will need to pull from more data sources.'}
        </Label>
        {!dirty && (
          <Button className={classes.copyLink} color="secondary" variant="text" onClick={copyLink}>
            Copy Offer Link
          </Button>
        )}
      </div>
      <Grid container spacing={4} alignItems="center" justifyContent="center">
        {hasAtLeastMVRToPull && (
          <Grid item>
            <Label type="formLabelLight">
              Which reports do you want to run?
              <Button
                color="secondary"
                variant="text"
                className={classes.viewMoreButton}
                onClick={() => window?.Appcues?.show(DataPullsAppcuesID)}
              >
                View More
                <img src={arrowIcon} alt="arrow" />
              </Button>
            </Label>

            {/* This is not a FormField because we don't want it to be messing with the formik state */}
            <Radio
              id="dataPulls"
              mode="light"
              options={[
                { value: DataPulls.NonMVRPulls, label: 'Non-MVR reports' },
                { value: DataPulls.AllPulls, label: 'All reports' }
              ]}
              xs={12}
              value={selectedDataPulls}
              onChange={({ target: { value } }) => {
                setSelectedDataPulls(value);
                store.setFormAction({
                  action:
                    value === DataPulls.AllPulls
                      ? FormAction.FillSkippedDataPullsAll
                      : FormAction.FillSkippedDataPullsNonMVR
                });
              }}
              disabled={store.loading}
            />
          </Grid>
        )}
        <Grid item>
          <Button
            onClick={onClick}
            disabled={store.loading}
            loading={store.loading}
            variant="contained"
            color="primary"
            className={classes.btn}
          >
            <Label className={classes.btnLabel}>
              {getButtonLabel({ hasAtLeastMVRToPull, hasOnlyMVRToRun, selectedDataPulls })}
            </Label>
          </Button>
        </Grid>
      </Grid>
    </>
  );
});

export default FillSkippedDataPulls;
